import { feathers } from "@feathersjs/feathers";
import socketio from "@feathersjs/socketio-client";
import authentication from "@feathersjs/authentication-client";
import io from "socket.io-client";
import type { Application, TransportConnection } from "@feathersjs/feathers";
// import rest from '@feathersjs/rest-client';

// Assuming createClient is a function you need to import from a specific module
// import { createClient } from '../../api/src/client'; // Adjust the path as necessary
declare const window: Window;

interface ConnectionInstances {
  [key: string]: Application; // Add an index signature
}

const createClient = (
  connection: TransportConnection,
  authenticationOptions = {},
) => {
  const client = feathers();

  client.configure(connection);
  client.configure(authentication(authenticationOptions));
  client.set("connection", connection);
  return client;
};

const connectionInstances: ConnectionInstances = {};

const devUri = "http://localhost:3378";
const stageUri = "https://stage.ntxprecast-api.intrasync.app";
const prodUri = "https://ntxprecast-api.intrasync.app";

type Mode =
  | "dev"
  | "development"
  | "stage"
  | "beta"
  | "live"
  | "prod"
  | "production"
  | "test";

const uriMap: Record<Mode, string> = {
  dev: devUri,
  development: devUri,
  stage: stageUri,
  beta: stageUri,
  live: prodUri,
  prod: prodUri,
  production: prodUri,
  test: "",
};

interface ApiOptions {
  debugMode?: boolean;
  authStorageKey?: string;
  refreshStorageKey?: string;
  enableSocketAuth?: boolean;
  restClient?: boolean;
  enableFindOne?: boolean;
  storage?: Storage | LocalForage;
  uriOverride?: Record<string, string>;
}

interface SocketOptions {
  path: string;
  transports: string[];
  forceNew: boolean;
  pingInterval: number;
  pingTimeout: number;
  timeout?: number;
  query?: {
    Authorization?: string;
  };
}

interface FirstLoadAuth {
  accessToken?: string;
  refreshToken?: string;
  deviceId?: string;
  userId?: string;
  authPath?: string;
}

interface AuthOptions {
  path?: string;
  storageKey: string;
  storage: Storage | LocalForage;
}

/**
 * Check if a JWT token is expired
 * @param token - JWT token string
 * @returns true if expired or invalid, false if valid
 */
const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return true;

    const payload = JSON.parse(atob(parts[1]));
    if (!payload.exp) return true;

    // Check if token expires within 60 seconds (add buffer for network latency)
    const expiryTime = payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const bufferTime = 60 * 1000; // 60 second buffer

    return currentTime >= expiryTime - bufferTime;
  } catch {
    return true;
  }
};

/**
 * Refresh the access token using the refresh token
 * @param uri - API base URI
 * @param refreshToken - Refresh token
 * @param userId - User ID
 * @param deviceId - Device ID
 * @returns New access token or null if refresh failed
 */
const refreshAccessToken = async (
  uri: string,
  refreshToken: string,
  userId: string,
  deviceId: string,
): Promise<string | null> => {
  try {
    const refreshUrl = `${uri}/refresh-token`;
    console.log("Refreshing expired access token before socket connection...", {
      url: refreshUrl,
      userId,
      deviceId,
      hasRefreshToken: !!refreshToken,
      refreshTokenLength: refreshToken?.length,
    });

    const response = await fetch(refreshUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refreshToken,
        userId: Number(userId),
        deviceId,
      }),
    });

    console.log("Token refresh response:", {
      status: response.status,
      ok: response.ok,
      statusText: response.statusText,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "Unknown error");
      console.warn("Token refresh HTTP failed:", response.status, errorText);
      return null;
    }

    const data = await response.json();
    console.log("Token refresh data:", {
      hasAccessToken: !!data.accessToken,
      accessTokenLength: data.accessToken?.length,
      keys: Object.keys(data),
    });

    if (data.accessToken) {
      console.log("Successfully refreshed access token");
      return data.accessToken;
    }

    console.warn("Token refresh response missing accessToken:", data);
    return null;
  } catch (error) {
    console.warn("Token refresh network error:", error);
    return null;
  }
};

const apiClient = (
  mode: Mode,
  apiOptions: ApiOptions = {
    debugMode: false,
    authStorageKey: "feathers-jwt-dev",
    refreshStorageKey: "refresh-token-dev",
    enableSocketAuth: true,
    restClient: false,
    enableFindOne: true,
  },
): Application => {
  let socketAuthentication = false;
  const {
    debugMode,
    authStorageKey,
    refreshStorageKey,
    storage,
    uriOverride,
    // enableFindOne,
  } = apiOptions;

  if (debugMode) {
    console.warn("apiClient debugMode enabled");
    console.warn("apiClient debugMode apiOptions", apiOptions);
  }
  // console.log('feathers in', mode, 'mode');

  if (debugMode) {
    console.warn("apiClient debugMode: authStorageKey", authStorageKey);
  }
  if (connectionInstances[mode]) {
    if (debugMode) {
      console.warn(
        "apiClient debugMode: connectionInstances[mode]",
        mode,
        connectionInstances[mode],
      );
    }
    return connectionInstances[mode];
  } else {
    if (mode === "test") {
      connectionInstances[mode] = feathers().configure(authentication());
      if (debugMode) {
        console.warn(
          "apiClient debugMode: connectionInstances[mode]",
          mode,
          connectionInstances[mode],
        );
      }
      return connectionInstances[mode];
    }
    let uri = uriMap[mode];
    if (debugMode) {
      console.warn("apiClient debugMode: uri", uri);
    }
    if (uriOverride && Object.keys(uriOverride).length > 0) {
      const uriWithOverrides = { ...uriMap, ...uriOverride };
      uri = uriWithOverrides[mode];
      if (debugMode) {
        console.warn("apiClient debugMode: uriOverride set uri", uri);
      }
    }
    const authStorage = storage ? storage : window.localStorage;
    if (debugMode) {
      console.warn("apiClient debugMode: authStorage", authStorage);
    }
    let authOptions: AuthOptions = {
      storageKey: authStorageKey || "intrasync-jwt-dev",
      storage: authStorage,
    };

    const authPath = `/authentication`; // defaults to /authentication
    if (debugMode) {
      console.warn(
        "apiClient debugMode: Adding authPath to authOptions.path",
        authPath,
      );
    }
    authOptions = {
      ...authOptions,
      // path: authPath,
    };

    if (debugMode) {
      console.warn("apiClient debugMode: authOptions", authOptions);
    }

    // if (apiOptions.restClient) {
    //   const apiRestClient = rest(uri);

    //   connectionInstances[mode] = feathers()
    //     .configure(apiRestClient.fetch(fetch))
    //     .configure(authentication(authOptions));

    //   if (debugMode) {
    //     console.warn(
    //       'apiClient debugMode: connectionInstances[mode]',
    //       mode,
    //       connectionInstances[mode]
    //     );
    //   }
    //   return connectionInstances[mode];
    // }

    let socketOptions: SocketOptions = {
      path: "/socket.io",
      // Prefer WebSocket, but fall back to HTTP long-polling when the WS upgrade
      // is unavailable (ingress not upgrading `Upgrade:` requests, restrictive
      // proxy, mid-deploy). Without a fallback the client hard-fails and cannot
      // refresh its JWT — every service call then 401s. See
      // docs/websocket-transport-outage-devops.md for the required server-side fix
      // and the load-balancer session-affinity caveat that polling depends on.
      transports: ["websocket", "polling"],
      forceNew: true,
      pingInterval: 25000, // Increased for stability
      pingTimeout: 60000, // Increased timeout
      timeout: 10000, // Increased connection timeout
    };
    const firstLoadAuth: FirstLoadAuth = {};

    // Resolve storage keys - handle both "dev" and "development" modes
    const resolvedAuthStorageKey =
      authStorageKey ||
      (mode === "development"
        ? "feathers-jwt-development"
        : `feathers-jwt-${mode}`);
    const resolvedRefreshStorageKey =
      refreshStorageKey ||
      (mode === "development"
        ? "refresh-token-development"
        : `refresh-token-${mode}`);

    if (debugMode) {
      console.warn("apiClient debugMode: socketOptions", socketOptions);
    }

    if (apiOptions.enableSocketAuth && window && window.localStorage) {
      if (debugMode) {
        console.warn("apiClient debugMode: window", window);
      }

      try {
        // Batch localStorage reads for better performance
        const userIdKey = mode.includes("prod") ? "userId" : `userId-${mode}`;

        const [accessToken, refreshToken, deviceId, userId] = [
          window.localStorage.getItem(resolvedAuthStorageKey),
          window.localStorage.getItem(resolvedRefreshStorageKey),
          window.localStorage.getItem("deviceId"),
          window.localStorage.getItem(userIdKey),
        ];

        if (debugMode) {
          console.warn("apiClient debugMode: batched auth data", {
            hasAccessToken: !!accessToken,
            hasRefreshToken: !!refreshToken,
            hasDeviceId: !!deviceId,
            hasUserId: !!userId,
          });
        }

        // Validate tokens before using them
        const isValidToken = (token: string | null): boolean => {
          if (!token) return false;
          try {
            // Basic JWT validation - check if it has proper structure
            const parts = token.split(".");
            return parts.length === 3 && parts.every((part) => part.length > 0);
          } catch {
            return false;
          }
        };

        if (isValidToken(accessToken)) {
          firstLoadAuth.accessToken = accessToken!;
        }

        if (isValidToken(refreshToken)) {
          firstLoadAuth.refreshToken = refreshToken!;
        }

        if (deviceId) {
          firstLoadAuth.deviceId = deviceId;
        }

        if (userId) {
          firstLoadAuth.userId = userId;
        }

        if (authOptions && authOptions.path) {
          firstLoadAuth.authPath = authOptions.path;
        }

        // Only setup socket auth if we have minimum required data
        if (
          firstLoadAuth.deviceId &&
          (firstLoadAuth.refreshToken || firstLoadAuth.accessToken)
        ) {
          try {
            const Auth = JSON.stringify(firstLoadAuth);
            // Limit auth payload size to prevent socket issues
            if (Auth.length < 2048) {
              socketOptions = {
                ...socketOptions,
                query: {
                  Authorization: Auth,
                },
              };
              socketAuthentication = true;

              if (debugMode) {
                console.warn("apiClient debugMode: socket auth enabled", {
                  authPayloadSize: Auth.length,
                  hasToken: !!firstLoadAuth.accessToken,
                  hasRefresh: !!firstLoadAuth.refreshToken,
                });
              }
            } else {
              console.warn(
                "apiClient: Auth payload too large, skipping socket auth",
              );
            }
          } catch (error) {
            console.warn("apiClient: Failed to serialize auth data:", error);
          }
        } else {
          if (debugMode) {
            console.warn(
              "apiClient debugMode: insufficient auth data for socket auth",
              {
                hasDeviceId: !!firstLoadAuth.deviceId,
                hasTokens: !!(
                  firstLoadAuth.refreshToken || firstLoadAuth.accessToken
                ),
              },
            );
          }
        }
      } catch (error) {
        console.warn(
          "apiClient: Error setting up socket authentication:",
          error,
        );
      }
    }

    // Check if access token is expired and needs refresh before connecting
    let tokenNeedsRefresh = false;
    if (
      firstLoadAuth.accessToken &&
      isTokenExpired(firstLoadAuth.accessToken)
    ) {
      tokenNeedsRefresh = true;
    }

    // Create socket with autoConnect: false so we can refresh token first if needed
    const socket = io(uri, {
      ...socketOptions,
      // autoConnect: !tokenNeedsRefresh, // Only auto-connect if token is fresh
    });

    // If token needs refresh, do it async then connect
    if (
      tokenNeedsRefresh &&
      firstLoadAuth.refreshToken &&
      firstLoadAuth.userId &&
      firstLoadAuth.deviceId
    ) {
      (async () => {
        const newAccessToken = await refreshAccessToken(
          uri,
          firstLoadAuth.refreshToken!,
          firstLoadAuth.userId!,
          firstLoadAuth.deviceId!,
        );

        if (newAccessToken) {
          // Store the new token in localStorage
          window.localStorage.setItem(resolvedAuthStorageKey, newAccessToken);

          // Update firstLoadAuth with new token
          firstLoadAuth.accessToken = newAccessToken;
          console.log("Connecting socket with refreshed token...");
        } else {
          console.warn(
            "Token refresh failed, connecting with refresh token only",
          );
          // Keep the refresh token in firstLoadAuth so backend can use it
        }

        // Update the socket query before connecting
        // socket.io.opts.query doesn't work for initial connect, need to update the manager's opts
        const authPayload = JSON.stringify(firstLoadAuth);

        // For socket.io v4+, update the query on the manager's options
        if (socket.io && socket.io.opts) {
          socket.io.opts.query = { Authorization: authPayload };
        }

        // Also set auth as a backup (socket.io v4+ recommended approach)
        socket.auth = { Authorization: authPayload };

        // Now connect the socket
        socket.connect();
      })();
    }

    // Handle reconnection - refresh token if needed before reconnecting
    socket.io.on("reconnect_attempt", async () => {
      const currentToken = window.localStorage.getItem(resolvedAuthStorageKey);
      const currentRefreshToken = window.localStorage.getItem(
        resolvedRefreshStorageKey,
      );
      const currentUserId = window.localStorage.getItem(
        mode.includes("prod") ? "userId" : `userId-${mode}`,
      );
      const currentDeviceId = window.localStorage.getItem("deviceId");

      if (
        currentToken &&
        isTokenExpired(currentToken) &&
        currentRefreshToken &&
        currentUserId &&
        currentDeviceId
      ) {
        const newToken = await refreshAccessToken(
          uri,
          currentRefreshToken,
          currentUserId,
          currentDeviceId,
        );

        if (newToken) {
          window.localStorage.setItem(resolvedAuthStorageKey, newToken);
          // Update socket query for reconnection
          const updatedAuth: FirstLoadAuth = {
            accessToken: newToken,
            refreshToken: currentRefreshToken,
            userId: currentUserId,
            deviceId: currentDeviceId,
          };
          socket.io.opts.query = {
            Authorization: JSON.stringify(updatedAuth),
          };
          console.log("Reconnecting with refreshed token");
        }
      }
    });

    // Add connection event handlers for better debugging
    if (debugMode) {
      socket.on("connect", () => {
        console.warn("apiClient debugMode: Socket connected");
      });

      socket.on("disconnect", (reason) => {
        console.warn("apiClient debugMode: Socket disconnected:", reason);
      });

      socket.on("connect_error", (error) => {
        console.warn("apiClient debugMode: Socket connection error:", error);
      });
    }

    if (debugMode) {
      console.warn("apiClient debugMode: socket", socket);
    }

    const socketConnection = socketio(socket, {
      timeout: 100000,
    });

    // connectionInstances[mode] = feathers()
    //   .configure(
    //     socketio(socket, {
    //       timeout: 100000,
    //     })
    //   )
    //   .configure(authentication(authOptions));

    const newClient = createClient(socketConnection, authOptions);

    // Only log in debug mode
    if (debugMode) {
      console.warn("apiClient debugMode: newClient", newClient);
    }
    connectionInstances[mode] = newClient;

    // if (enableFindOne) {
    //   connectionInstances[mode].mixins.push(function (service: any) {
    //     service.findOne = function (params: any) {
    //       params = params || {};
    //       params.query = params.query || {};
    //       params.query.$limit = 1;
    //       return service.find(params).then(function (result: any) {
    //         const data = result.data || result;
    //         return Array.isArray(data) ? data[0] : data;
    //       });
    //     };
    //   });
    // }

    if (debugMode) {
      console.warn(
        "apiClient debugMode: connectionInstances[mode]",
        mode,
        connectionInstances[mode],
      );
    }
    if (
      socketAuthentication &&
      connectionInstances[mode] &&
      "set" in connectionInstances[mode]
    ) {
      connectionInstances[mode].set("socketAuthPending", true);
      if (debugMode) {
        console.warn(
          'apiClient debugMode: connectionInstances[mode].set("socketAuthPending", true);',
        );
      }
    }
    return connectionInstances[mode];
  }
};

/**
 * export default apiClient
 * @param {String} mode Server Mode: dev, development, stage, beta, prod, production
 * @param {Object} apiOptions
 *  Default:
 *  {
 *    debugMode: false,
 *    authStorageKey: 'feathers-jwt-dev',
 *    refreshStorageKey: 'refresh-token-dev',
 *    enableSocketAuth: true,
 *    restClient: false,
 *    enableFindOne: false
 *  }
 * @returns Api Client
 */
export default apiClient;
